import React, { useMemo } from "react";
import { useTable, useSortBy, useFilters, usePagination } from "react-table";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa";

const NewsAndSentimentTable = ({
  data = [],
  currencyPair,
  timeZone,
  styles = {},
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    console.error("No data provided to NewsAndSentimentTable.");
    return <p>No news or sentiment data available for {currencyPair}.</p>;
  }

  const columns = useMemo(
    () => [
      {
        Header: "Title",
        accessor: "title",
        Cell: ({ row }) => {
          const { title, link } = row.original;
          return (
            <a href={link} target="_blank" rel="noopener noreferrer">
              {title || "No Title"}
            </a>
          );
        },
      },
      {
        Header: "Author",
        accessor: "author",
        Cell: ({ value }) => value || "Unknown Author",
      },
      {
        Header: "Published",
        accessor: "timestamp",
        Cell: ({ value }) =>
          value
            ? moment(value).tz(timeZone).format("YYYY-MM-DD HH:mm:ss z")
            : "Unknown Date",
      },
      {
        Header: "Sentiment",
        accessor: "sentiment",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Sentiment Score",
        accessor: "sentiment_score",
        Cell: ({ value }) => (value !== undefined ? value.toFixed(2) : "N/A"),
      },
      {
        Header: "Topic",
        accessor: "topic",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Topic Score",
        accessor: "topic_score",
        Cell: ({ value }) => (value !== undefined ? value.toFixed(2) : "N/A"),
      },
    ],
    [timeZone]
  );

  /**
   * Use the pagination plugin
   */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    pageCount,
    gotoPage,
    state: { pageIndex },
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  function SelectColumnFilter({ column: { filterValue, setFilter, preFilteredRows, id } }) {
    const options = useMemo(() => {
      const uniqueValues = new Set(preFilteredRows.map((row) => row.values[id]));
      return [...uniqueValues].filter(Boolean);
    }, [id, preFilteredRows]);

    return (
      <select
        value={filterValue || ""}
        onChange={(e) => setFilter(e.target.value || undefined)}
      >
        <option value="">All</option>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className={styles?.tableContainer || "table-container"}>
      {/* Pagination controls at the top, aligned to the right */}
      <div className="d-flex justify-content-end align-items-center my-2">
        {/* "Go to first page" button */}
        <button
          className="btn btn-outline-primary btn-sm mx-1"
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
        >
          {"<<"}
        </button>

        {/* Previous page */}
        <button
          className="btn btn-outline-primary btn-sm mx-1"
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        >
          {"<"}
        </button>

        {/* Next page */}
        <button
          className="btn btn-outline-primary btn-sm mx-1"
          onClick={() => nextPage()}
          disabled={!canNextPage}
        >
          {">"}
        </button>

        {/* "Go to last page" button */}
        <button
          className="btn btn-outline-primary btn-sm mx-1"
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
        >
          {">>"}
        </button>

        {/* Page Info */}
        <span className="mx-2">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
      </div>

      {/* Table */}
      <div className="table-responsive" style={{ marginTop: "1rem" }}>
        <table
          {...getTableProps()}
          className={`table table-striped table-hover ${
            styles?.table || "news-table"
          }`}
          style={{
            minWidth: "600px",
          }}
        >
          <thead className="table-dark" style={{ verticalAlign: "top" }}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => {
                  let sortIcon = <FaSort style={{ marginLeft: 4 }} />;
                  if (column.isSorted) {
                    sortIcon = column.isSortedDesc ? (
                      <FaSortDown style={{ marginLeft: 4 }} />
                    ) : (
                      <FaSortUp style={{ marginLeft: 4 }} />
                    );
                  }
                  return (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.id}
                      style={{
                        verticalAlign: "top",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        {column.render("Header")}
                        <span>{column.canSort && sortIcon}</span>
                      </div>
                      {column.canFilter && column.Filter ? (
                        <div style={{ marginTop: 4 }}>{column.render("Filter")}</div>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {/* Render the current "page" of rows */}
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={row.id}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} key={cell.column.id}>
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// PropTypes validation
NewsAndSentimentTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
      author: PropTypes.string,
      timestamp: PropTypes.string,
      sentiment: PropTypes.string,
      sentiment_score: PropTypes.number,
      topic: PropTypes.string,
      topic_score: PropTypes.number,
    })
  ).isRequired,
  currencyPair: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  styles: PropTypes.object,
};

export default NewsAndSentimentTable;
